import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import SEO from "../components/SEO";
import { opgaverData } from "../data/siteData";
import { useContactDrawer } from "../context/ContactDrawerContext";

export default function OpgaverDetail() {
  const { taskSlug } = useParams();
  const { openDrawer } = useContactDrawer();
  const [lightboxImage, setLightboxImage] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const opgave = opgaverData.find((item) => item.slug === taskSlug);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxImage || !opgave?.galleryImages) return;
      if (e.key === "Escape") {
        setLightboxImage(null);
      } else if (e.key === "ArrowRight") {
        const nextIdx = (lightboxIndex + 1) % opgave.galleryImages.length;
        setLightboxIndex(nextIdx);
        setLightboxImage(opgave.galleryImages[nextIdx].src);
      } else if (e.key === "ArrowLeft") {
        const prevIdx = (lightboxIndex - 1 + opgave.galleryImages.length) % opgave.galleryImages.length;
        setLightboxIndex(prevIdx);
        setLightboxImage(opgave.galleryImages[prevIdx].src);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [lightboxImage, lightboxIndex, opgave]);

  if (!opgave) {
    return (
      <div className="pt-32 pb-20 min-h-[60vh] flex flex-col items-center justify-center bg-brand-sand-50 px-6">
        <SEO title="Opgave ikke fundet | Elmegaarden Have og Anlæg" />
        <h1 className="text-2xl md:text-3xl font-bold text-brand-green-900 mb-4">
          Opgaven blev ikke fundet
        </h1>
        <p className="text-brand-charcoal/70 mb-8">
          Vi kunne desværre ikke finde den ønskede opgave.
        </p>
        <Link
          to="/opgaver"
          className="px-6 py-3 bg-brand-green-700 text-white font-bold text-sm tracking-wide rounded-sm hover:bg-brand-green-800 transition-colors"
        >
          Se alle opgaver
        </Link>
      </div>
    );
  }

  const primaryImage = opgave.galleryImages?.[0]?.src || "https://assets.auxodev.dk/assets/77df7d82-25aa-453c-8f7b-e2453151bb33/elmegaarden-0003-0.jpg";

  return (
    <div className="pt-[72px] bg-brand-sand-50 min-h-screen">
      <SEO
        title={opgave.title}
        description={opgave.metaDescription}
        ogImage={primaryImage}
        canonical={`/opgaver/${opgave.slug}`}
      />

      {/* Hero Banner */}
      <section className="relative bg-brand-green-950 text-white pt-24 pb-16 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={primaryImage.replace(
              "https://assets.auxodev.dk/assets/",
              "https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/"
            )}
            alt={opgave.heading}
            className="w-full h-full object-cover opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-brand-green-950 via-brand-green-950/70 to-brand-green-950/40" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="max-w-3xl text-left">
            <Link
              to="/opgaver"
              className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-brand-sand-300 hover:text-white mb-6 transition-colors"
            >
              <i className="fa-solid fa-arrow-left text-[10px]" /> Alle opgaver
            </Link>
            <h1 className="font-display text-3xl md:text-5xl font-bold text-white mb-4">
              {opgave.heading}
            </h1>
            {opgave.subtitle && (
              <p className="text-lg md:text-xl text-neutral-200 font-light">
                {opgave.subtitle}
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Main Content Area */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Main Body (Left Column) */}
          <div className="lg:col-span-8 flex flex-col gap-10 text-left">
            {opgave.sections.map((section, idx) => (
              <div key={idx} className="flex flex-col gap-4">
                {section.heading && (
                  <h2 className="font-display text-2xl md:text-3xl font-bold text-brand-green-900 leading-snug">
                    {section.heading}
                  </h2>
                )}
                {section.text && (
                  <div className="text-brand-charcoal/85 leading-relaxed text-base space-y-4 whitespace-pre-line">
                    {section.text}
                  </div>
                )}
                {section.bullets && (
                  <ul className="list-disc list-inside space-y-2 text-brand-charcoal/85 text-base font-medium pl-2 my-2">
                    {section.bullets.map((bullet, bIdx) => (
                      <li key={bIdx} className="leading-relaxed">
                        {bullet}
                      </li>
                    ))}
                  </ul>
                )}
                {section.afterText && (
                  <div className="text-brand-charcoal/85 leading-relaxed text-base space-y-4 whitespace-pre-line">
                    {section.afterText}
                  </div>
                )}
              </div>
            ))}

            {/* Quick Action Buttons */}
            <div className="pt-6 border-t border-brand-sand-200 flex flex-wrap items-center gap-4">
              <button
                onClick={openDrawer}
                className="px-6 py-3.5 bg-[#7ca632] hover:bg-[#6b9128] text-white font-bold text-xs sm:text-sm tracking-wide uppercase rounded-sm transition-all duration-300 shadow-sm cursor-pointer"
              >
                Kontakt os i dag
              </button>
              <a
                href="tel:+4520661517"
                className="px-6 py-3.5 bg-brand-green-900 hover:bg-brand-green-800 text-white font-bold text-xs sm:text-sm tracking-wide uppercase rounded-sm transition-all duration-300 shadow-sm flex items-center gap-2"
              >
                <i className="fa-solid fa-phone" /> Ring til os: 20 66 15 17
              </a>
            </div>

            {/* Gallery Section */}
            {opgave.galleryImages && opgave.galleryImages.length > 0 && (
              <div className="mt-12 pt-10 border-t border-brand-sand-200">
                <h3 className="font-display text-2xl font-bold text-brand-green-900 mb-6">
                  Billeder fra vores projekter
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  {opgave.galleryImages.map((img, iIdx) => (
                    <div
                      key={iIdx}
                      onClick={() => {
                        setLightboxImage(img.src);
                        setLightboxIndex(iIdx);
                      }}
                      className="group aspect-square rounded-sm overflow-hidden bg-neutral-100 border border-brand-sand-200 shadow-xs hover:shadow-md transition-all duration-300 cursor-pointer relative"
                    >
                      <img
                        src={img.src.replace(
                          "https://assets.auxodev.dk/assets/",
                          "https://assets.auxodev.dk/cdn-cgi/image/width=400,height=400,fit=cover,quality=80,format=auto/assets/"
                        )}
                        alt={img.alt || `${opgave.heading} billede ${iIdx + 1}`}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                        <i className="fa-solid fa-magnifying-glass-plus text-white text-lg" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar (Right Column) */}
          <div className="lg:col-span-4 flex flex-col gap-8">
            {/* Opgaver Navigation */}
            <div className="bg-white p-6 md:p-8 rounded-sm border border-brand-sand-200 shadow-xs text-left">
              <h3 className="font-display text-lg font-bold text-brand-green-900 mb-6 pb-3 border-b border-brand-sand-100">
                Vores opgaver
              </h3>
              <nav className="flex flex-col gap-2">
                {opgaverData.map((item) => {
                  const isCurrent = item.slug === taskSlug;
                  return (
                    <Link
                      key={item.slug}
                      to={`/opgaver/${item.slug}`}
                      className={`flex items-center justify-between p-3 rounded-sm text-sm font-semibold transition-all duration-300 ${
                        isCurrent
                          ? "bg-brand-green-900 text-white"
                          : "bg-brand-sand-50 text-brand-charcoal hover:bg-brand-sand-100 hover:text-brand-green-900"
                      }`}
                    >
                      <span>{item.menuTitle}</span>
                      <i
                        className={`fa-solid fa-chevron-right text-xs ${
                          isCurrent ? "text-brand-sand-300" : "text-brand-sand-400"
                        }`}
                      />
                    </Link>
                  );
                })}
                <Link
                  to="/andre-opgaver"
                  className="flex items-center justify-between p-3 rounded-sm text-sm font-semibold transition-all duration-300 bg-brand-sand-50 text-brand-charcoal hover:bg-brand-sand-100 hover:text-brand-green-900"
                >
                  <span>Andre opgaver</span>
                  <i className="fa-solid fa-chevron-right text-xs text-brand-sand-400" />
                </Link>
              </nav>
            </div>

            {/* Direct Contact Info Box */}
            <div className="bg-brand-green-900 text-white p-8 rounded-sm shadow-md text-left relative overflow-hidden">
              <div className="relative z-10 flex flex-col gap-5">
                <h3 className="font-display text-xl font-bold text-white">
                  Kontakt Elmegaarden
                </h3>
                <p className="text-sm text-neutral-200 leading-relaxed">
                  Vi kører i Hobro, Arden, Hadsund og hele Nordjylland. Kontakt os i dag og få en faglig vurdering af dit projekt.
                </p>
                <div className="flex flex-col gap-3 text-sm text-neutral-100 pt-2 border-t border-white/10">
                  <div className="flex items-center gap-3">
                    <i className="fa-solid fa-phone text-[#7ca632] w-5 text-center" />
                    <a href="tel:+4520661517" className="font-bold hover:text-[#7ca632] transition-colors">
                      +45 20 66 15 17
                    </a>
                  </div>
                  <div className="flex items-center gap-3">
                    <i className="fa-solid fa-envelope text-[#7ca632] w-5 text-center" />
                    <a
                      href="mailto:rasmus@elmegaardenhaveoganlaeg.dk"
                      className="hover:text-[#7ca632] transition-colors break-all"
                    >
                      rasmus@elmegaardenhaveoganlaeg.dk
                    </a>
                  </div>
                  <div className="flex items-center gap-3">
                    <i className="fa-solid fa-location-dot text-[#7ca632] w-5 text-center" />
                    <span>Stenstrupvej 5, 9500 Hobro</span>
                  </div>
                </div>
                <button
                  onClick={openDrawer}
                  className="mt-4 w-full py-3.5 bg-[#7ca632] hover:bg-[#6b9128] text-white font-bold text-xs uppercase tracking-wider rounded-sm transition-all duration-300 cursor-pointer shadow-sm"
                >
                  Få et uforpligtende tilbud
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {lightboxImage && opgave.galleryImages && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-sm transition-opacity duration-300 p-4"
          onClick={() => setLightboxImage(null)}
        >
          <button
            onClick={() => setLightboxImage(null)}
            className="absolute top-6 right-6 text-white hover:text-brand-sand-300 text-2xl p-2 cursor-pointer z-50"
            aria-label="Luk billede"
          >
            <i className="fa-solid fa-xmark" />
          </button>

          {opgave.galleryImages.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                const prevIdx =
                  (lightboxIndex - 1 + opgave.galleryImages.length) % opgave.galleryImages.length;
                setLightboxIndex(prevIdx);
                setLightboxImage(opgave.galleryImages[prevIdx].src);
              }}
              className="absolute left-6 text-white hover:text-brand-sand-300 text-3xl p-4 cursor-pointer z-50"
              aria-label="Forrige billede"
            >
              <i className="fa-solid fa-chevron-left" />
            </button>
          )}

          {opgave.galleryImages.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                const nextIdx = (lightboxIndex + 1) % opgave.galleryImages.length;
                setLightboxIndex(nextIdx);
                setLightboxImage(opgave.galleryImages[nextIdx].src);
              }}
              className="absolute right-6 text-white hover:text-brand-sand-300 text-3xl p-4 cursor-pointer z-50"
              aria-label="Næste billede"
            >
              <i className="fa-solid fa-chevron-right" />
            </button>
          )}

          <div
            className="max-w-5xl max-h-[85vh] flex flex-col items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={lightboxImage.replace(
                "https://assets.auxodev.dk/assets/",
                "https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=90,format=auto/assets/"
              )}
              alt="Forstørret billede"
              className="max-w-full max-h-[75vh] object-contain rounded-sm shadow-2xl"
            />
            <div className="mt-4 text-center text-white">
              <p className="text-sm font-medium">
                {opgave.galleryImages[lightboxIndex]?.alt || opgave.heading}
              </p>
              <p className="text-xs text-neutral-400 mt-1">
                {lightboxIndex + 1} af {opgave.galleryImages.length}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
